/**
 * Автоматические развёртки стен по чертежу.
 *
 * Развёртка — это вид на стену изнутри помещения: прямоугольник во всю высоту
 * стены, на нём проёмы и точки (розетки, выключатели). Ничего дополнительного
 * пользователь не чертит: развёртка полностью выводится из плана, поэтому
 * подвинули стену на чертеже — развёртка перестроилась.
 *
 * Две вещи, которые здесь решаются:
 *
 * 1. **Ширина развёртки — по ЧИСТОЙ грани, а не по осям.** Стоя в комнате,
 *    человек видит стену от угла до угла в свету, поэтому берём ребро
 *    внутреннего контура комнаты (`room.inner`), а не осевую линию.
 *
 * 2. **Куда смотрит развёртка.** Наблюдатель стоит внутри комнаты лицом к стене.
 *    Его «вправо» — это левая нормаль к направлению взгляда. Отсюда получается,
 *    совпадает ось развёртки с направлением стены a → b или смотрит против него.
 *    Без этого половина развёрток оказалась бы зеркальной.
 *
 * Все размеры — в миллиметрах, начало координат развёртки — левый нижний угол
 * стены (пол, левый угол комнаты).
 */
import {
	add,
	dist,
	leftNormal,
	mul,
	norm,
	pointInPolygon,
	projectOnSegment,
	sub,
	type Pt,
} from "@/shared/lib/geometry";
import { CatalogAttach, getCatalogItem } from "@/shared/catalog";
import {
	itemDimensions,
	type Fixture,
	type FloorItem,
	type Opening,
	type WallGraph,
} from "@/shared/plan";
import type { Room } from "./rooms";
import { openingsOfWall } from "./walls";

/** Проём в координатах развёртки. */
export interface ElevationOpening {
	opening: Opening;
	x0: number;
	x1: number;
	/** Низ проёма от пола */
	y0: number;
	/** Верх проёма от пола */
	y1: number;
}

/** Точка на стене в координатах развёртки. */
export interface ElevationFixture {
	fixture: Fixture;
	x: number;
	y: number;
}

/** Напольный предмет, показанный фасадом на ближайшей стене комнаты. */
export interface ElevationFloorItem {
	item: FloorItem;
	x: number;
	y: number;
	width: number;
	height: number;
	/**
	 * Какой стороной предмет к наблюдателю: передом или задом — «front», боком — «side».
	 * Сзади рисуем тот же вид спереди, только зеркально: силуэт у них один.
	 */
	view: "front" | "side";
	/** Рисунок отразить по горизонтали: правый край предмета оказался слева на развёртке */
	mirror: boolean;
}

/** Одна стена помещения, развёрнутая в плоскость. */
export interface WallElevation {
	wallId: string;
	/** Номер стены в помещении, с единицы — им она подписана на чертеже */
	index: number;
	/** Ширина по чистой грани, мм */
	width: number;
	/** Высота стены, мм */
	height: number;
	/** Толщина стены слева и справа — их показываем в разрезе по краям развёртки */
	leftThickness: number;
	rightThickness: number;
	openings: ElevationOpening[];
	fixtures: ElevationFixture[];
	floorItems: ElevationFloorItem[];
	/** x развёртки = origin + dir · (смещение вдоль стены от узла a) */
	origin: number;
	dir: 1 | -1;
	/** Направление оси развёртки в координатах плана. */
	axis: Pt;
	/** Грань стены, обращённая в это помещение: с ней сравниваем Fixture.side */
	side: 1 | -1;
}

/** Смещение вдоль стены от узла a по координате развёртки. */
export const offsetFromElevationX = (elevation: WallElevation, x: number): number =>
	(x - elevation.origin) * elevation.dir;

/** Координата развёртки по смещению вдоль стены. */
export const elevationXFromOffset = (elevation: WallElevation, offset: number): number =>
	elevation.origin + elevation.dir * offset;

/**
 * Строит развёртки всех стен помещения — в порядке обхода его контура.
 * Стены — из графа, объекты на стенах — списком (вызывающий решает, из каких слоёв).
 */
export const buildRoomElevations = (
	plan: WallGraph,
	allFixtures: Fixture[],
	room: Room,
	allFloorItems: FloorItem[] = [],
): WallElevation[] => {
	const count = room.inner.length;
	if (count < 3) return [];

	const elevations: WallElevation[] = [];
	const floorItemsByEdge = new Map<number, FloorItem[]>();
	for (const item of allFloorItems) {
		const catalogItem = getCatalogItem(item.ref);
		if (catalogItem?.attach !== CatalogAttach.Everywhere) continue;
		const point = { x: item.x, y: item.y };
		if (!pointInPolygon(point, room.inner)) continue;

		let nearestEdge = -1;
		let nearestDistance = Number.POSITIVE_INFINITY;
		for (let edgeIndex = 0; edgeIndex < room.inner.length; edgeIndex++) {
			const from = room.inner[edgeIndex];
			const to = room.inner[(edgeIndex + 1) % room.inner.length];
			const { distance } = projectOnSegment(point, from, to);
			if (distance < nearestDistance) {
				nearestDistance = distance;
				nearestEdge = edgeIndex;
			}
		}
		if (nearestEdge >= 0) {
			const edgeItems = floorItemsByEdge.get(nearestEdge) ?? [];
			edgeItems.push(item);
			floorItemsByEdge.set(nearestEdge, edgeItems);
		}
	}

	for (let i = 0; i < count; i++) {
		const wall = plan.walls[room.wallIds[i]];
		if (!wall) continue;
		const a = plan.nodes[wall.a];
		const b = plan.nodes[wall.b];
		if (!a || !b) continue;

		const from = room.inner[i];
		const to = room.inner[(i + 1) % count];
		const width = dist(from, to);
		if (width < 1) continue;

		const edge = norm(sub(to, from));
		// Нормаль, смотрящая внутрь комнаты: проверяем пробной точкой от середины ребра.
		const probe = 20;
		const candidate = leftNormal(edge);
		const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
		const inward = pointInPolygon(add(middle, mul(candidate, probe)), room.inner)
			? candidate
			: mul(candidate, -1);

		// Наблюдатель смотрит на стену изнутри; его «вправо» — левая нормаль взгляда.
		// Отсюда ось развёртки: вдоль ребра обхода или против него.
		const flipped = !pointsSameWay(inward, candidate);
		const axis = flipped ? mul(edge, -1) : edge;
		// При развороте оси левым краем развёртки становится другой угол стены.
		const base = flipped ? to : from;

		const along = norm(sub(b, a));
		const dir: 1 | -1 = dot(along, axis) >= 0 ? 1 : -1;
		const origin = dot(sub(a, base), axis);

		// Грань стены, смотрящая в комнату
		const side: 1 | -1 = dot(leftNormal(along), inward) >= 0 ? 1 : -1;

		const toX = (offset: number) => origin + dir * offset;

		const openings: ElevationOpening[] = [];
		for (const opening of openingsOfWall(plan, wall.id)) {
			const center = toX(opening.offset);
			const x0 = center - opening.width / 2;
			const x1 = center + opening.width / 2;
			// Проём соседней стены может частично выйти за развёртку — не показываем то,
			// что в эту комнату не выходит.
			if (x1 <= 0 || x0 >= width) continue;
			openings.push({
				opening,
				x0: Math.max(0, x0),
				x1: Math.min(width, x1),
				y0: opening.sill,
				y1: Math.min(wall.height, opening.sill + opening.height),
			});
		}

		const fixtures: ElevationFixture[] = [];
		for (const fixture of allFixtures) {
			if (fixture.wallId !== wall.id) continue;
			// Розетка висит на одной из двух граней стены — на развёртке видно только свою
			if (fixture.side !== side) continue;
			const x = toX(fixture.offset);
			if (x < -50 || x > width + 50) continue;
			fixtures.push({ fixture, x, y: fixture.elevation });
		}
		fixtures.sort((p, q) => p.x - q.x);

		const floorItems: ElevationFloorItem[] = [];
		for (const item of floorItemsByEdge.get(i) ?? []) {
			const catalogItem = getCatalogItem(item.ref);
			if (catalogItem === null) continue;
			const dimensions = itemDimensions(item, catalogItem);
			const projection = projectOnSegment({ x: item.x, y: item.y }, from, to);
			const edgeAngle = Math.atan2(axis.y, axis.x);
			const rotation = (item.rotation * Math.PI) / 180;
			const itemWidth =
				Math.abs(Math.cos(rotation - edgeAngle)) * dimensions.width +
				Math.abs(Math.sin(rotation - edgeAngle)) * dimensions.depth;
			// Оси предмета на плане: вправо — +x символа, вперёд — +y (символы рисуют перед внизу).
			// Наблюдатель стоит в комнате, то есть со стороны `inward`
			const right = { x: Math.cos(rotation), y: Math.sin(rotation) };
			const forward = { x: -Math.sin(rotation), y: Math.cos(rotation) };
			const facesViewer = Math.abs(dot(forward, inward)) >= Math.abs(dot(right, inward));
			// Спереди правый край предмета у наблюдателя справа, сбоку перед рисунка смотрит вправо
			const mirror = facesViewer
				? dot(right, axis) < 0 !== (item.mirrored ?? false)
				: dot(forward, axis) < 0;
			floorItems.push({
				item,
				x: Math.max(0, Math.min(width, dot(sub(projection.point, base), axis))),
				y: item.elevation ?? catalogItem.elevation,
				width: itemWidth,
				height: dimensions.height,
				view: facesViewer ? "front" : "side",
				mirror,
			});
		}

		// Соседние стены: previous приходит в угол `from`, next — в угол `to`.
		// «Слева» и «справа» — уже в координатах развёртки, поэтому при развороте
		// оси соседи меняются местами.
		const previous = plan.walls[room.wallIds[(i - 1 + count) % count]];
		const next = plan.walls[room.wallIds[(i + 1) % count]];
		const before = flipped ? next : previous;
		const after = flipped ? previous : next;

		elevations.push({
			wallId: wall.id,
			index: i + 1,
			width,
			height: wall.height,
			leftThickness: before?.thickness ?? 0,
			rightThickness: after?.thickness ?? 0,
			openings,
			fixtures,
			floorItems,
			origin,
			dir,
			axis,
			side,
		});
	}

	return elevations;
};

const dot = (p: Pt, q: Pt) => p.x * q.x + p.y * q.y;
const pointsSameWay = (p: Pt, q: Pt) => dot(p, q) >= 0;
